import React, { useCallback, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Edit, ArrowRight } from "lucide-react";
import { getHoldingHistory, getTransactionHistory } from "@/api";
import { HoldingAudit, TransactionAudit } from "@/types";

type AuditEntry = HoldingAudit | TransactionAudit;

interface AuditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  entityId: number;
  entityType: "holding" | "transaction";
  isLoading?: boolean;
  renderCurrentData?: (data: AuditEntry) => React.ReactNode;
}

interface FieldChange {
  field: string;
  label: string;
  oldValue: any;
  newValue: any;
}

export const AuditSheet = ({
  open,
  onOpenChange,
  title,
  entityId,
  entityType,
  isLoading = false,
  renderCurrentData,
}: AuditSheetProps) => {
  const [auditHistory, setAuditHistory] = React.useState<AuditEntry[]>([]);

  // Helper function to format timestamp
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  // Helper function to compare two audit entries and find changes
  const getChangedFields = (current: AuditEntry, previous: AuditEntry): FieldChange[] => {
    const changes: FieldChange[] = [];

    // Helper function to format base amount
    const formatBaseAmount = (val: number, entry: AuditEntry) => `${entry.currency} ${val.toLocaleString()}`;
    const formatQuantity = (val: number) => val.toLocaleString();
    const formatConfirmed = (val: boolean) => val ? 'Yes' : 'No';
    const formatTransactionDate = (val: string) => new Date(val).toLocaleDateString();

    // Common fields to compare
    const fieldsToCompare = [
      { key: 'baseAmount', label: 'Base Amount' },
      { key: 'quantity', label: 'Quantity' },
      { key: 'currency', label: 'Currency' },
      { key: 'accountName', label: 'Account', fallback: 'accountKey' },
      { key: 'instrumentName', label: 'Instrument', fallback: 'instrumentKey' },
      { key: 'isConfirmed', label: 'Confirmed' },
    ];

    // Add transaction-specific fields
    if (entityType === "transaction") {
      fieldsToCompare.push(
        { key: 'transactionDate', label: 'Transaction Date' },
        { key: 'transactionType', label: 'Transaction Type' },
        { key: 'description', label: 'Description' }
      );
    }

    fieldsToCompare.forEach(({ key, label, fallback }) => {
      const currentValue = (current as any)[key] || (fallback ? (current as any)[fallback] : undefined);
      const previousValue = (previous as any)[key] || (fallback ? (previous as any)[fallback] : undefined);

      if (currentValue !== previousValue) {
        let formattedOldValue = previousValue;
        let formattedNewValue = currentValue;

        // Format values based on the field type
        if (key === 'baseAmount') {
          formattedOldValue = previousValue !== undefined ? formatBaseAmount(previousValue, previous) : previousValue;
          formattedNewValue = currentValue !== undefined ? formatBaseAmount(currentValue, current) : currentValue;
        } else if (key === 'quantity') {
          formattedOldValue = previousValue !== undefined ? formatQuantity(previousValue) : previousValue;
          formattedNewValue = currentValue !== undefined ? formatQuantity(currentValue) : currentValue;
        } else if (key === 'isConfirmed') {
          formattedOldValue = previousValue !== undefined ? formatConfirmed(previousValue) : previousValue;
          formattedNewValue = currentValue !== undefined ? formatConfirmed(currentValue) : currentValue;
        } else if (key === 'transactionDate') {
          formattedOldValue = previousValue !== undefined ? formatTransactionDate(previousValue) : previousValue;
          formattedNewValue = currentValue !== undefined ? formatTransactionDate(currentValue) : currentValue;
        }

        changes.push({
          field: key,
          label,
          oldValue: formattedOldValue,
          newValue: formattedNewValue
        });
      }
    });

    return changes;
  };

  const getHistory = useCallback(async () => {
    switch (entityType) {
      case "holding":
        const holdings = await getHoldingHistory(entityId);
        setAuditHistory(holdings);
        break;
      case "transaction":
        const transactions = await getTransactionHistory(entityId);
        setAuditHistory(transactions);
        break;
      default:
        console.warn(`Unsupported entity type: ${entityType}`);
        setAuditHistory([]);
        break;
    }
  }, [entityId, entityType]);

  useEffect(() => {
    if (open) {
      getHistory();
    }
  }, [getHistory, open]);

  const renderAuditEntry = (entry: AuditEntry, index: number) => {
    const isFirst = index === 0;
    const previousEntry = !isFirst ? auditHistory[index - 1] : null;
    const changes = previousEntry ? getChangedFields(entry, previousEntry) : [];

    return (
      <div className="space-y-2">
        {/* Show changes if this isn't the first entry */}
        {!isFirst && changes.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
            <div className="flex items-center gap-2 mb-2">
              <Edit className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-blue-900">Changes Made</span>
              <Badge variant="secondary" className="text-xs">
                {changes.length} field{changes.length !== 1 ? 's' : ''} modified
              </Badge>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
              {changes.map((change) => (
                <div key={change.field} className="flex items-center gap-1 text-sm bg-white p-2 rounded">
                  <span className="font-medium text-gray-700 min-w-0 flex-shrink-0">{change.label}:</span>
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    <span className="text-red-600 bg-red-50 px-1 py-0.5 rounded text-xs truncate">
                      {change.oldValue || 'Empty'}
                    </span>
                    <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                    <span className="text-green-600 bg-green-50 px-1 py-0.5 rounded text-xs truncate">
                      {change.newValue || 'Empty'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Show current state */}
        <div className={`p-2 rounded-md ${isFirst ? 'bg-green-50 border border-green-200' : 'bg-muted'}`}>
          {isFirst && (
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-green-600 text-xs">Initial Entry</Badge>
            </div>
          )}
          {renderCurrentData ? renderCurrentData(entry) : renderDefaultData(entry)}
        </div>
      </div>
    );
  };

  const renderDefaultData = (entry: AuditEntry) => {
    const fields = [
      { label: 'Base Amount', value: `${entry.currency} ${entry.baseAmount.toLocaleString()}` },
      { label: 'Quantity', value: entry.quantity.toLocaleString() },
      { label: 'Account', value: entry.accountName || entry.accountKey },
      { label: 'Instrument', value: entry.instrumentName || entry.instrumentKey },
      { label: 'Confirmed', value: entry.isConfirmed ? "Yes" : "No" },
      { label: 'Last Updated', value: new Date(entry.updatedAt).toLocaleString() },
    ];

    // Add transaction-specific fields
    if (entityType === "transaction" && "transactionDate" in entry) {
      fields.splice(4, 0, 
        { label: 'Transaction Date', value: new Date(entry.transactionDate).toLocaleDateString() },
        { label: 'Transaction Type', value: entry.transactionType }
      );
      
      if ("description" in entry && entry.description) {
        fields.splice(6, 0, { label: 'Description', value: entry.description });
      }
    }

    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-1 text-sm">
        {fields.map((field, index) => (
          <div key={index} className="flex flex-col">
            <span className="font-semibold text-gray-700 text-xs">{field.label}:</span>
            <span className="text-gray-900 truncate" title={field.value}>{field.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[80vh] flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            {title} Audit History
          </SheetTitle>
          <SheetDescription>
            View all changes made to {title.toLowerCase()} ID: {entityId}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  Loading audit history...
                </p>
              </div>
            </div>
          ) : auditHistory.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">
                No audit history available
              </p>
            </div>
          ) : (
            <ScrollArea className="h-full">
              <div className="space-y-3 p-3">
                {auditHistory.map((entry, index) => (
                  <Card key={`${entry.eventTime}-${index}`} className="w-full">
                    <CardHeader className="pb-2 pt-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          {formatTimestamp(entry.eventTime)}
                        </CardTitle>
                        {index === 0 && (
                          <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                            Most Recent
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {renderAuditEntry(entry, index)}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
